import React from 'react';
import { Avatar, Box, Card, CardContent, Chip, Divider, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import type { PayrollCalculation } from '../../../../hooks/usePayrollRun';
import { getAvatarColors, getInitials } from '../../../../utils/avatarUtils';
import { PAYROLL_RUN_COLUMNS, getPayrollRunColumnValue, type PayrollRunColumnKey } from '../payrollRunColumns';

interface PayrollRunMobileProps {
	employees: PayrollCalculation[];
	currentPage: number;
	itemsPerPage: number;
	formatEUR: (value: number | null | undefined) => string;
	onViewDetails: (employee: PayrollCalculation) => void;
	isColumnVisible: (columnId: PayrollRunColumnKey) => boolean;
}

// One card per employee, showing every column the desktop table can show (filtered by the same
// column-visibility state via `isColumnVisible`) — matches the desktop table 1:1 rather than a
// reduced mobile-only field set (see docs/PAYROLL_RUN_LAYOUT.md).
const PayrollRunMobile: React.FC<PayrollRunMobileProps> = ({
	employees,
	currentPage,
	itemsPerPage,
	formatEUR,
	onViewDetails,
	isColumnVisible,
}) => {
	const { t } = useTranslation();
	const paginatedEmployees = React.useMemo(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		return employees.slice(startIndex, startIndex + itemsPerPage);
	}, [employees, currentPage, itemsPerPage]);

	if (employees.length === 0) {
		return (
			<Box sx={{ textAlign: 'center', py: 6 }}>
				<CalculateIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
				<Typography variant="body1" color="text.secondary">{t('payroll.run.table.empty.title')}</Typography>
				<Typography variant="body2" color="text.disabled">{t('payroll.run.table.empty.description')}</Typography>
			</Box>
		);
	}

	const detailColumns = PAYROLL_RUN_COLUMNS.filter((c) => c.id !== 'status' && isColumnVisible(c.id));

	return (
		<Stack spacing={1.5}>
			{paginatedEmployees.map((employee) => {
				const [employeeFirstName, ...employeeLastNameParts] = employee.name.split(' ');
				const employeeLastName = employeeLastNameParts.join(' ');
				const avatarColors = getAvatarColors(`${employee.name}${employee.employeeNumber}`);
				return (
					<Card key={employee.employeeNumber} variant="outlined" sx={{ borderRadius: 1 }}>
						<CardContent sx={{ pb: '16px !important' }}>
							<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
									<Avatar sx={{
										width: 36,
										height: 36,
										flexShrink: 0,
										bgcolor: avatarColors.bg,
										color: avatarColors.color,
										fontSize: '0.875rem',
										fontWeight: 600,
									}}>
										{getInitials(employeeFirstName, employeeLastName)}
									</Avatar>
									<Box sx={{ minWidth: 0, textAlign: 'left' }}>
										<Typography variant="subtitle2" fontWeight={600} noWrap>{employee.name}</Typography>
										<Typography variant="caption" color="text.secondary">{employee.employeeNumber}</Typography>
									</Box>
								</Box>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
									{isColumnVisible('status') && (
										<Chip label={t('payroll.run.statusOk')} size="small" color="success" variant="outlined"/>
									)}
									<Tooltip title={t('payroll.run.table.header.details')}>
										<IconButton
											size="small"
											aria-label={t('payroll.run.mobile.ariaViewDetails', { name: employee.name })}
											onClick={() => onViewDetails(employee)}
											sx={{ bgcolor: 'action.hover', '&:hover': { bgcolor: 'action.selected' } }}
										>
											<InfoOutlinedIcon fontSize="small" />
										</IconButton>
									</Tooltip>
								</Box>
							</Box>

							{detailColumns.length > 0 && (
								<>
									<Divider sx={{ my: 1 }} />
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
										{detailColumns.map((column) => (
											<Box key={column.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
												<Typography variant="caption" color="text.secondary">{t(column.labelKey)}</Typography>
												<Typography variant="caption" fontWeight={500}>
													{getPayrollRunColumnValue(employee, column.id, formatEUR, t)}
												</Typography>
											</Box>
										))}
									</Box>
								</>
							)}
						</CardContent>
					</Card>
				);
			})}
		</Stack>
	);
};

export default PayrollRunMobile;
